import { GQL } from "@binale-tech/shared";

import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";

import { withThinking } from "../runner/runAgent";
import type { AgentContext, AgentDetailItem, AgentStep, ReportLine } from "../runner/types";

const DIRECTIONS = [GQL.IPostingRuleDirection.Incoming, GQL.IPostingRuleDirection.Outgoing] as const;

// Mirrors the backend's assertCanWriteDirection: outgoing gates on Debitoren, incoming on every other booking product
const canWriteDirection = (ctx: AgentContext, direction: GQL.IPostingRuleDirection): boolean =>
    direction === GQL.IPostingRuleDirection.Outgoing
        ? ctx.canWrite(GQL.IProductKey.Deb)
        : ProductAccessUtils.getAccountingProductKeyList()
              .filter((p) => p !== GQL.IProductKey.Deb)
              .some((p) => ctx.canWrite(p));

export const postingRulesStep: AgentStep = {
    id: "postingRules",
    async run(ctx, io) {
        const work: { direction: GQL.IPostingRuleDirection; count: number }[] = [];
        for (const direction of DIRECTIONS) {
            if (io.isCancelled()) break;
            if (!canWriteDirection(ctx, direction)) {
                io.narrateSkipped(
                    direction === GQL.IPostingRuleDirection.Outgoing ? GQL.IProductKey.Deb : GQL.IProductKey.Er,
                );
                continue;
            }
            const count = await withThinking(io, "app.agent.postingRules.thinking", () =>
                ctx.readPostingRuleSuggestionCount(direction),
            );
            if (count > 0) work.push({ direction, count });
        }
        if (work.length === 0) return { status: "nothing" };

        const approved = await io.approve({
            textId: "app.agent.postingRules.approve.question",
            values: { count: work.reduce((sum, w) => sum + w.count, 0) },
            goLabelId: "app.agent.postingRules.approve.go",
            learnMoreId: "app.agent.postingRules.learnMore",
        });
        if (!approved) return { status: "skipped", byUser: true };

        let imported = 0;
        const details: AgentDetailItem[] = [];
        for (const { direction } of work) {
            if (io.isCancelled()) break;
            const rules = await withThinking(io, "app.agent.postingRules.importing", () =>
                ctx.importPostingRuleSuggestions(direction),
            );
            imported += rules.length;
            const textId =
                direction === GQL.IPostingRuleDirection.Outgoing
                    ? ("app.agent.details.postingRules.outgoing" as const)
                    : ("app.agent.details.postingRules.incoming" as const);
            for (const rule of rules) {
                details.push({
                    group: "done",
                    ...(rule.contactId ? { ref: { kind: "contact" as const, id: String(rule.contactId) } } : {}),
                    outcome: {
                        textId,
                        values: { counterparty: rule.counterpartyName || "-", account: rule.categoryNum },
                    },
                });
            }
        }
        if (imported === 0) return { status: "nothing" };
        io.post({ kind: "done", textId: "app.agent.postingRules.done", values: { count: imported }, details });
        const summary: ReportLine[] = [
            { textId: "app.agent.report.postingRulesImported", values: { count: imported } },
        ];
        return { status: "done", summary, details };
    },
};
